import React, { useMemo } from 'react';
import { styled, TextV2 } from '@withjoy/joykit';
import { EventConfiguration } from '@apps/workspace/Workspace';
import { useEventPhoto } from '@shared/utils/eventPhoto';
import { useFormatEventDate } from '@shared/hooks/useFormatEventDate';

const CountdownContainer = styled.div<{ eventPhoto: string }>`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.eventPhoto});
`;

const CountdownContent = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;
  min-height: 350px;
  padding: 32px;
  align-items: center;
  display: flex; /* Makes the parent a flex container */
  flex-direction: column; /* Arranges items in a column */
  justify-content: flex-end;
  gap: 8px;
`;

const GradientBackground = styled.div`
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  z-index: 1;
  mask: linear-gradient(transparent, transparent, black);
  background-color: rgba(0, 0, 0, 1);
`;

const GradientBlur = styled.div`
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  position: absolute;
  z-index: 2;
  mask: linear-gradient(transparent, transparent, black);
  backdrop-filter: blur(4px);
`;

const DEFAULT_IMAGE = 'https://dev.withjoy.com/media/386f4bd5-4ee5-4bca-98f6-0663b0504f34/aee8faf0-8aa0-11f0-8f00-056e9c2af0b0-Unsplash_seanwsinclair_C_NJKfnTR5A.jpg';

export const EventCountdown: React.FC<{ eventConfiguration?: EventConfiguration }> = ({ eventConfiguration }) => {
  const { eventPhoto, loading } = useEventPhoto(eventConfiguration?.eventHandle);
  const eventPhotoUrl = eventPhoto?.url ?? DEFAULT_IMAGE;
  const locationInfo = eventConfiguration?.locationInfo;

  const eventDateInfo = useMemo(() => {
    if (!eventConfiguration) return null;
    return {
      timezone: eventConfiguration.timezone,
      finalizedDate: eventConfiguration.finalizedDate,
      tentativeDate: eventConfiguration.tentativeDate,
    };
  }, [eventConfiguration]);

  const date = useFormatEventDate(eventDateInfo, 'dd MMMM yyyy', { useTentativeFallback: true });

  if (loading) {
    return <TextV2>Loading...</TextV2>;
  }

  return (
    <CountdownContainer eventPhoto={eventPhotoUrl}>
      <GradientBackground />
      <GradientBlur />
      <CountdownContent>
        <TextV2
          width={'100%'}
          typographyVariant={'CalloutHed32Bold'}
          color={'rgba(255,255,255,0.95)'}
          style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.3)' }}
          textAlign={'center'}
        >
          {eventConfiguration?.eventDisplayName ?? 'Untitled Event'}
        </TextV2>
        {date && (
          <TextV2
            width={'100%'}
            color={'rgba(255,255,255,0.95)'}
            style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.3)' }}
            textAlign={'center'}
            typographyVariant={'body4'}
          >
            {date}
          </TextV2>
        )}
        {locationInfo && (
          <TextV2
            width={'100%'}
            color={'rgba(255,255,255,0.95)'}
            style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.3)' }}
            textAlign={'center'}
            typographyVariant={'body4'}
          >
            {`${locationInfo.city}, ${locationInfo.state ? locationInfo.state : locationInfo.country ? locationInfo.country : ''}`}
          </TextV2>
        )}
      </CountdownContent>
    </CountdownContainer>
  );
};
